import { getReferralUrlWithPromo } from '@frontend/api-client';
import { identity, match } from '@frontend/duck-tape';
import {
  MEMBER_REFERRAL_COPY_DEFAULT_VALUE,
  useCallback,
  useCopyToClipboard,
  useEffect,
  useFeatureValue,
  useMemo,
} from '@frontend/react';
import {
  ActionIcon,
  Button,
  Icon,
  Image,
  PageContainer,
  Pill,
  Table,
  Text,
  TextInput,
  Title,
  XStack,
  YStack,
  showToast,
  useBreakpoints,
} from '@frontend/web-react';
import { useAccessControl, useAnalytics, useRetrieveClientQuery, useRetrieveReferralsQuery } from '@frontend/web/hooks';
import { createFileRoute } from '@tanstack/react-router';

const InviteFriends = () => {
  const { data: client, isLoading: isClientLoading } = useRetrieveClientQuery();
  const { data: referrals, isLoading: isReferralsLoading } = useRetrieveReferralsQuery();
  const { track } = useAnalytics();
  const { checkAccessAndRedirect, isClientSubscribed } = useAccessControl();
  const { value: memberReferralCopy } = useFeatureValue('member_referral_copy', MEMBER_REFERRAL_COPY_DEFAULT_VALUE);
  const { isMobile } = useBreakpoints();
  useEffect(() => {
    checkAccessAndRedirect();
  }, [isClientSubscribed]);

  const referralURlWithPromo = useMemo(() => {
    return getReferralUrlWithPromo(client?.referralUrl);
  }, [client, isClientLoading]);

  const writeReferralUrlToClipboard = useCallback((text: string) => navigator.clipboard.writeText(text), []);

  const handleCopyReferralUrlError = useCallback(
    () => showToast({ title: 'Something went wrong copying your link', variant: 'danger' }),
    [],
  );

  const handleCopyReferralUrlSuccess = useCallback(() => {
    track({ event: 'referralLinkCopied', topic: 'invite' });
  }, [track]);

  const {
    copyToClipboard: copyReferralUrlToClipboard,
    isCopied: isReferralUrlCopied,
    isDisabled: isCopyReferralUrlStatusDisabled,
    isLoading: isCopyReferralUrlLoading,
  } = useCopyToClipboard({
    copy: writeReferralUrlToClipboard,
    onCopyError: handleCopyReferralUrlError,
    onCopySuccess: handleCopyReferralUrlSuccess,
  });

  const copyReferralUrl = useCallback(() => {
    if (!referralURlWithPromo) return;
    void copyReferralUrlToClipboard(referralURlWithPromo);
  }, [copyReferralUrlToClipboard, referralURlWithPromo]);

  const isCopyReferralUrlDisabled = isCopyReferralUrlStatusDisabled || !referralURlWithPromo;
  const copyReferralUrlButtonLabel = isReferralUrlCopied ? 'Link copied' : 'Copy Link';

  return (
    <PageContainer
      className="bg-neutral-50"
      gap="lg"
      isLoading={isClientLoading || isReferralsLoading}
      isScrollable={false}
    >
      {/* Copy referral link and qr code section */}
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <XStack
        className="p-lg mt-lg w-full rounded-lg bg-cover bg-center"
        flexShrink={1}
        gap="lg"
        style={{ backgroundImage: 'url(/sky.jpg)' }}
      >
        <YStack className="max-w-full gap-md">
          <XStack alignItems="center" gap="md">
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <Image className="size-[42px]" source="/new_logo.svg" />
            <Title className="text-neutral-950" type={isMobile ? 'h3' : 'h1'} uppercase>
              Give the gift of Duckbill
            </Title>
          </XStack>
          <Text className="max-w-full text-neutral-950">{memberReferralCopy}</Text>
          <YStack className="mt-md max-w-full gap-md">
            <Text className="max-w-full text-neutral-950">
              Copy your own personalized referral link below, then send to a friend:
            </Text>
            <XStack className="gap-md flex-col sm:flex-row items-start sm:items-center">
              <TextInput
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="w-full"
                onChange={identity}
                readOnly
                rightSection={
                  <ActionIcon
                    aria-label="Copy referral link"
                    disabled={isCopyReferralUrlDisabled}
                    loading={isCopyReferralUrlLoading}
                    onClick={copyReferralUrl}
                    variant="white"
                  >
                    <Icon name="IconCopy" />
                  </ActionIcon>
                }
                value={referralURlWithPromo}
              />
              <Button
                className="w-full sm:w-fit"
                disabled={isCopyReferralUrlDisabled}
                isLoading={isCopyReferralUrlLoading}
                label={copyReferralUrlButtonLabel}
                onClick={copyReferralUrl}
                type="button"
              />
            </XStack>
          </YStack>
        </YStack>
      </XStack>

      {/* List of sent invites */}
      <YStack gap="lg">
        <Title type="h5">Review Invites</Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          {referrals?.map((referral) => {
            const statusColor = match(referral.status)
              .with('Pending', () => 'lime')
              .with('Accepted', () => 'green')
              .with('Waitlist', () => 'blue')
              .otherwise(() => 'gray');

            return (
              <Table.Tr key={referral.referredName + referral.status}>
                <Table.Td>{referral.referredName}</Table.Td>
                <Table.Td>
                  <Pill classNames={{ label: `font-medium text-${statusColor}-900`, root: `bg-${statusColor}-200` }}>
                    {referral.status}
                  </Pill>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table>
        {!referrals?.length && (
          <YStack className="items-center" gap="lg">
            <Icon isLoading={false} name="IconMoodConfuzed" size="lg" stroke={2} />
            <Text>{"You haven't shared any invites yet"}</Text>
          </YStack>
        )}
      </YStack>
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/settings/invite-friends')({
  component: InviteFriends,
});
